/**
 * userService.js — Kullanıcılar (fds users, saha/admin tarafı) yönetimi.
 *
 * Base: {JAVA_BACKEND_API_URL}/users — FDS-Backend'in UserController'ı (canlı, önceden vardı).
 *
 * Backlog #301 (2026-10-08): controller'ın her ucu artık oturum istiyor (getAuthHeader). Yazmalar
 * (oluştur / düzenle / sil / grup değiştir) ve `GET /users` 'Kullanıcı Rolü Atama'
 * (MANAGE_USER_ROLES) yetkisi ister — Admin'de var, kök her yetkiye sahip. Listeler personel
 * içindir; tek kullanıcı okuması kendisi ya da personel içindir. Yanıtlar artık şifre hash'ini ve
 * TOTP sırrını hiç taşımıyor; yine de `userLoginPassword` alanı ASLA state'e alınmaz / render
 * edilmez, getUser/updateUser bu dosyanın içinde temizler.
 * WAS: "⚠️ Bu controller'da neredeyse hiç auth kontrolü yok (bkz. FDS-Backend CLAUDE.md "Known
 * rough edges")."
 *
 * `getStaffUsers` sadece firma-dışı (staff) kullanıcıları döner — `GET /users` tüm tabloyu
 * (21k+ firma girişi dahil) döktüğü için burada kullanılmıyor.
 * Backlog #379 (2026-10-13): Kullanıcılar sayfası artık `getKullaniciListesi` (sunucu sayfalı, her giriş) ve
 * `getKullaniciKarti` ile çalışıyor; `getStaffUsers` sayfadan çıktı (Yetki Yönetimi hâlâ çağırıyor, duruyor).
 * WAS (#379, 2026-10-13): `createUser` / `deleteUser` sayfadan çıkmıştı, çünkü kullanıcılar ESKİ SİSTEMDE (PHP) oluşturuluyordu,
 * sunucuda POST /users reddediliyordu (#306 1b) ve canlı giriş ad / soyad / e-posta / telefon / aktiflik / parolayı her girişte
 * PHP'den eziyordu. İkisi de ortadan kalktı (#523: giriş kendi tablomuzu okuyor, #524: POST /users açık), bu yüzden #545'te
 * `createUser` ve `updateUser` sayfaya döndü: Yeni Kullanıcı Ekle, Kullanıcı Kartı › Düzenle, Pasifleştir / Aktifleştir
 * (`userActive` 0 / 1, `updateUser` ile) ve Kişi Kartı › Kullanıcı Oluştur. `deleteUser` HÂLÂ sayfadan uzak: backend kullanıcının
 * rol / fuar / firma bağlarını da kalıcı siler, bu yüzden silme yerine pasifleştirme var (Deniz, #545).
 *
 * `updateUser` (PUT /users/update/{id}): #301'den beri backend yalnızca gövdede GELEN alanları
 * değiştiriyor (UserService.updateUserFields). Eskiden tam bir UserEntity bekleyip gönderilmeyen
 * alanları BeanUtils.copyProperties ile SIFIRLIYORDU (int alanlar 0'a, diğerleri null'a). Önce
 * mevcut kaydı çekip düzenlenen alanları üzerine yazan birleştirme BİLEREK duruyor: frontend,
 * backend'den önce yayına çıkarsa eski backend'e de doğru gövde gitsin. Şifre alanı bu
 * birleşimden kasıtlı olarak çıkarılıyor; boş/eksik gönderilirse backend mevcut hash'i
 * dokunmadan bırakıyor.
 */
import axios from 'axios';
import { JAVA_BACKEND_API_URL, getAuthHeader } from '../Components/Contexts/BackendConstants';

const BASE = `${JAVA_BACKEND_API_URL}/users`;

function parseError(error) {
  const message =
    error.response?.data?.message ||
    (typeof error.response?.data === 'string' ? error.response.data : null) ||
    error.message ||
    'Bilinmeyen hata';
  const wrapped = new Error(message);
  wrapped.status = error.response?.status ?? null;
  throw wrapped;
}

function stripPassword(entity) {
  if (!entity || typeof entity !== 'object') return entity;
  const { userLoginPassword, ...safe } = entity;
  return safe;
}

// ─── Okumalar ───────────────────────────────────────────────────────────────

// Boş süzgeç alanlarını ('' / null) isteğe koymaz: uç boş değeri "süzgeç yok" yerine geçersiz sayabilir.
function compact(params) {
  const out = {};
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v === null || v === undefined || v === '') return;
    out[k] = v;
  });
  return out;
}

/**
 * Kullanıcılar sayfasının listesi (backlog #379, backend #378): HER giriş (firma tarafı dahil), sunucu tarafında
 * sayfalı. Süzgeç / sıralama / sayfalama `GET /rbac/users` ile AYNI (Yetki Yönetimi › Kullanıcılar'ın sorgusu):
 * `q, groupId, staff, active, firmaId, firmaVar, firmaNation, firmaTypeId, fuarId, girisVar, sonGirisBaslangic,
 * sonGirisBitis, sort (ad | sonGiris | kayit | rol), page (0-tabanlı), size`.
 * Satır = /rbac/users satırı + `kisi` (#377 bağlantısı ya da null: personId, adSoyad, fotografVar,
 * birlestirildigiPersonId, baglamaTarihi), `yabanci` (rozet: firmanın uyruğu, firması yoksa kullanıcının kendisi),
 * `uyruk` (üç kaynak + `uyumsuz`) ve `seciliFuar` (üst çubuktaki fuar ya da null). Yetki: page.kullanici.
 * @returns {{ total, page, size, users[] }}
 */
export async function getKullaniciListesi(params = {}) {
  try {
    const response = await axios.get(`${BASE}/kullanici-listesi`, { headers: getAuthHeader(), params: compact(params) });
    return response.data;
  } catch (error) {
    parseError(error);
  }
}

/**
 * Kullanıcı Kartı (backlog #379, backend #378): `{ kullanici (liste satırı), atandigiFuarlar[] (user_to_fuar),
 * firmaFuarlari[] (kullanıcının firmalarının çalıştığı fuarlar), kisiBaglantilari { aktif, gecmis[] },
 * kisiDuzenleyebilir }`. Giriş GEÇMİŞİ yok (hiçbir sistem tutmuyor): "giriş" = `kullanici.seciliFuar` + `userLastLogin`.
 * 404: kullanıcı yok. Yetki: page.kullanici.
 */
export async function getKullaniciKarti(id) {
  try {
    const response = await axios.get(`${BASE}/${id}/kart`, { headers: getAuthHeader() });
    return response.data;
  } catch (error) {
    parseError(error);
  }
}

/**
 * Telefon / e-posta tekrar raporu (backlog #491, backend #490): aynı telefonu (son 10 hane) ya da e-postayı (kırpılmış, küçük harf)
 * taşıyan kullanıcı grupları. Parametreler: `alan` TELEFON (varsayılan) | EPOSTA, `sadeceAktif` (varsayılan true), `ayniKisiGizle`
 * (varsayılan false), `page` (0-tabanlı), `size` (50, en çok 200). Yetki: page.kullanici.
 * → `{ alan, sadeceAktif, ayniKisiGizle, page, size, toplam, ozet { gruplar, kullanicilar, ayniKisiGruplari, ayniKisiKullanicilari,
 * cakismaGruplari }, gruplar[{ anahtar, kullaniciSayisi, aktifSayisi, ayniKisi, kullanicilar[] }] }`; gerçek çakışmalar önde.
 * Her kullanıcı satırı: `userId, kullaniciAdi, ad, soyad, adSoyad, unvan, telefon, eposta, aktif, sonGiris, uyrukId, userGroupId,
 * grupAdi, personel, firmaId, firmaAdi, firmaYabanci, firmaSayisi, bagliPersonId, bagliAdSoyad`.
 */
export async function getIletisimTekrarlari(params = {}) {
  try {
    const response = await axios.get(`${BASE}/iletisim-tekrarlari`, { headers: getAuthHeader(), params: compact(params) });
    return response.data;
  } catch (error) {
    parseError(error);
  }
}

export async function getStaffUsers() {
  try {
    const response = await axios.get(`${BASE}/staff`, { headers: getAuthHeader() });
    return response.data || [];
  } catch (error) {
    parseError(error);
  }
}

export async function getUserGroups() {
  try {
    const response = await axios.get(`${BASE}/groups/all`, { headers: getAuthHeader() });
    return response.data || [];
  } catch (error) {
    parseError(error);
  }
}

// Ham kayıt — sadece updateUser'ın güvenli birleştirmesi için, dışarı verilmez. (#301'den beri
// backend şifre hash'ini göndermiyor. WAS: "Şifre dahil ham kayıt".)
async function getUserRaw(id) {
  const response = await axios.get(`${BASE}/${id}`, { headers: getAuthHeader() });
  return response.data;
}

// ─── Yazmalar ───────────────────────────────────────────────────────────────

/**
 * POST /users?userGroupId= (MANAGE_USER_ROLES). Backend reddi olduğu gibi gelir (`.status` ile dallanılabilir):
 * 400 boş kullanıcı adı / parola (#544), 409 kullanıcı adını başka kullanıcı taşıyor (#544), 403 SuperAdmin rolü ya da
 * rbac.manage'siz personel rolü. Kullanıcı adı kırpılıp saklanır, büyük / küçük harf ayırt edilmez.
 * @param fields {userLoginName, userLoginPassword, userName, userSurname, userTitle, userPhone,
 *   userEmail, userActive, userNationalityId?, fuarId?, userValidPeriodDays?}
 * @param userGroupId yeni kullanıcının başlangıç grubu
 * @param firmaId (#628) isteğe bağlı: yeni giriş AYNI işlemde bu firmaya bağlanır (yalnızca firma tarafı rollerde; personel rolüyle 400,
 *   bilinmeyen firma 404). Yetki: MANAGE_USER_ROLES ya da kullanici.olustur (personel rolü her zaman MANAGE_USER_ROLES ister).
 *   Parola artık isteğe bağlı: boş = kayıtlı parola yok, giriş şifre bağlantısı / aktivasyon maili ile belirlenene dek kapalı.
 * #611 (backend #610): `fields.languageId` (1 TR / 2 EN, hiç yazılmazsa uyruktan / firmadan türetilir; başka değer 400) gövdede gider;
 *   `fields.portalRolIdleri` (portal rol numaraları, YALNIZCA firmaId ile, portal.kullanici_rol.manage ister: yoksa 403) gövdeye değil adrese
 *   yazılır ve girişin portal rolleri varsayılan rol YERİNE bunlar olur. Yeni backend'siz ikisi de sessizce yok sayılır.
 * @returns {{ id, userLoginName, userEntity, userId }} `userId` yeni kullanıcının numarası
 */
export async function createUser(fields, userGroupId, firmaId = null) {
  try {
    const { portalRolIdleri, ...govde } = fields || {};
    const payload = {
      userNationalityId: 1,
      fuarId: 0,
      userValidPeriodDays: 0,
      // Gövdede yoksa Java int'i 0 olur ve yeni kullanıcı pasif doğar.
      userActive: 1,
      ...govde,
    };
    const firmaParam = firmaId ? `&firmaId=${encodeURIComponent(firmaId)}` : '';
    const rolParam = firmaId && portalRolIdleri?.length ? portalRolIdleri.map((r) => `&portalRolIdleri=${encodeURIComponent(r)}`).join('') : '';
    const response = await axios.post(`${BASE}?userGroupId=${userGroupId}${firmaParam}${rolParam}`, payload, {
      headers: getAuthHeader(),
    });
    const data = response.data || {};
    const userEntity = stripPassword(data.userEntity);
    return { ...data, userEntity, userId: data.id ?? userEntity?.id ?? null };
  } catch (error) {
    parseError(error);
  }
}

/**
 * Girişin kayıtlı dili (#611, backend #610): 1 TR / 2 EN, hiç seçilmediyse null (uyruktan / firmadan türetilir). Kart satırında yoktur;
 * GET /users/{id} (personel) kaydındaki `languageId`dan okunur. PUT /users/update/{id} gövdesindeki `languageId` (1 / 2 / null) bunu değiştirir.
 */
export async function getKullaniciDili(id) {
  try {
    const kayit = await getUserRaw(id);
    return kayit?.languageId ?? null;
  } catch (error) {
    parseError(error);
  }
}

/**
 * Sadece değişen alanları gönderin (ör. {userName, userSurname, userPhone}); yeni bir şifre
 * belirlemek için userLoginPassword ekleyin, aksi halde göndermeyin/boş bırakın.
 *
 * Yanıt = kullanıcı JSON'u + `uyarilar[]` (#490): düzenleme telefonu / e-postayı başka kullanıcıların taşıdığı bir değere
 * TAŞIDIYSA `{ kod, mesaj, alan, kullanicilar[{ userId, kullaniciAdi, adSoyad, aktif }] }`; kayıt bu yüzden reddedilmez.
 * Backend reddi olduğu gibi gelir: 409 kullanıcı adı başkasında (#544) / kendini pasifleştirme, 403 SuperAdmin ya da kökün
 * hesabını başkası düzenliyor.
 */
export async function updateUser(id, fields) {
  try {
    const original = await getUserRaw(id);
    const { userLoginPassword: newPassword, ...rest } = fields || {};
    const merged = {
      ...original,
      ...rest,
      userLoginPassword: newPassword && newPassword.trim() ? newPassword.trim() : null,
    };
    // Kullanıcı adı boş kayıtlı kullanıcılar (NULL, yerelde 6) yeniden gönderilen null ile 400 alırdı (#544): değişmediyse gönderme.
    if (rest.userLoginName === undefined && !String(merged.userLoginName ?? '').trim()) delete merged.userLoginName;
    const response = await axios.put(`${BASE}/update/${id}`, merged, { headers: getAuthHeader() });
    return { ...stripPassword(response.data), uyarilar: response.data?.uyarilar || [] };
  } catch (error) {
    parseError(error);
  }
}

/**
 * Şifre bağlantısı oluştur (backlog #556, backend #555): POST /users/{id}/sifre-linki (MANAGE_USER_ROLES; kökün hesabını yalnızca
 * kökün kendisi). → `{ anahtar, gecerlilikBitis, gecerlilikGun, kullaniciAdi, kullaniciAktif }`. Anahtar YALNIZCA bu yanıtta gelir
 * (backend yalnızca SHA-256'sını tutar): çağıran ne saklar ne yazdırır, tam adres için `sifreBelirleAdresi(anahtar)`.
 * Yeni bağlantı o girişin önceki bağlantısını geçersiz kılar. Reddi olduğu gibi gelir: 403 kök hesap başkasında
 * (`sifre.linki_kok_yalniz_kendisi`) ya da yetkisiz, 404 kullanıcı yok.
 */
export async function createSifreLinki(id) {
  try {
    const response = await axios.post(`${BASE}/${id}/sifre-linki`, null, { headers: getAuthHeader() });
    return response.data;
  } catch (error) {
    parseError(error);
  }
}

/**
 * Aktivasyon maili — TEK kullanıcı (backlog #572, backend #568): POST /users/{id}/aktivasyon-maili (MANAGE_USER_ROLES; kökün hesabını yalnızca
 * kökün kendisi). Kullanıcıya yeni bir şifre belirleme bağlantısıyla mail gider (kendi dilinde, varsayılan göndericiden); bağlantı yanıtta ASLA yoktur.
 * → `{ sent, status SENT | FAILED | BLOCKED, to (maskeli), validUntil (yalnız gittiyse), language, inactive, message, error, logId }`.
 * BLOCKED (güvenli mod) ve FAILED da HTTP 200'dür: çağıran `status`a bakar. Gitmeyen posta kullanıcının önceki bağlantısını olduğu gibi bırakır.
 * 400: geçerli e-posta adresi yok, 404: kullanıcı yok, 403: yetkisiz ya da kökün hesabı başkasında.
 */
export async function sendAktivasyonMaili(id) {
  try {
    const response = await axios.post(`${BASE}/${id}/aktivasyon-maili`, null, { headers: getAuthHeader() });
    return response.data;
  } catch (error) {
    parseError(error);
  }
}

/**
 * Firma Detay › Kullanıcı ekle için giriş araması (backlog #628): GET /users/firma-kullanici-adaylari?q=&size= (MANAGE_USER_ROLES ya da
 * kullanici.olustur). YALNIZCA firma tarafı girişler (personel ve kök asla). → [{ userId, userLoginName, userName, userSurname, userEmail,
 * userActive, userGroupId, userGroupName, firmalar: [{ firmaId, firmaName }] }]. (Yetki Yönetimi'nin GET /rbac/users uçu rbac.manage ister.)
 */
export async function listFirmaKullaniciAdaylari({ q, size = 10 } = {}) {
  try {
    const response = await axios.get(`${BASE}/firma-kullanici-adaylari`, { headers: getAuthHeader(), params: { q, size } });
    return response.data || [];
  } catch (error) {
    parseError(error);
  }
}

/**
 * Firma tarafı bir girişi firmaya bağlar / başka firmadan taşır (backlog #628): PUT /users/{id}/firma, gövde { firmaId } (MANAGE_USER_ROLES ya da
 * kullanici.olustur). Girişin TEK firması olur; önceki firmadaki portal rolleri biter, varsayılan portal rolü verilir. → { userId, firmaIds,
 * oncekiFirmaIds }. 403 personel / kök hedef (o iş Yetki Yönetimi'nde kalır), 404 kullanıcı ya da firma yok.
 */
export async function setKullaniciFirma(id, firmaId) {
  try {
    const response = await axios.put(`${BASE}/${id}/firma`, { firmaId }, { headers: getAuthHeader() });
    return response.data;
  } catch (error) {
    parseError(error);
  }
}

/**
 * Aktivasyon maili — süzgece uyanlara (backlog #572, backend #571, GEÇİCİ). `params` Kullanıcılar listesinin KENDİ süzgeç parametreleridir
 * (`topluAktivasyonParametreleri`: q, groupId, active, firmaId, ... — sort / page / size yok); kimlik listesi ASLA gönderilmez, süzgeçsiz istek 400.
 *   · Önizleme (kuru çalıştırma): `{ matched, willSend, willSendNow, cap, capped, noEmail, badEmail, inactive, sentRecently, staff, root,
 *     sharedAddress, blockedBySafeMode, blockedReason, force, recentHours, runningJobId, warning }`. `zorla` son 24 saatte gidenleri de sayar.
 *   · Başlat: önizlemenin `willSendNow` sayısı `onayliSayi` olarak gider; 202 + işin ilerlemesi. 409: iş zaten çalışıyor / küme büyüdü / gidecek
 *     kimse yok / her posta güvenli modca engelli; 400: süzgeç yok ya da çok geniş / sayı yok.
 *   · Durum: `{ jobId, state RUNNING | DONE | CANCELLED | STOPPED, stoppedReason, total, processed, sent, failed, skipped, blocked, firstFailures[{ userId, reason }] }`.
 *   · İptal: çalışan işe "işlenen kullanıcıdan sonra dur" der, ilerlemeyi döner.
 */
export async function getTopluAktivasyonOnizleme(params, zorla = false) {
  try {
    const response = await axios.get(`${BASE}/aktivasyon-maili/toplu/onizleme`, {
      headers: getAuthHeader(), params: compact({ ...params, zorla: zorla ? true : undefined }),
    });
    return response.data;
  } catch (error) {
    parseError(error);
  }
}

export async function baslatTopluAktivasyon(params, zorla, onayliSayi) {
  try {
    const response = await axios.post(`${BASE}/aktivasyon-maili/toplu`, null, {
      headers: getAuthHeader(), params: compact({ ...params, zorla: zorla ? true : undefined, onayliSayi }),
    });
    return response.data;
  } catch (error) {
    parseError(error);
  }
}

export async function getTopluAktivasyonDurumu(jobId) {
  try {
    const response = await axios.get(`${BASE}/aktivasyon-maili/toplu/${encodeURIComponent(jobId)}`, { headers: getAuthHeader() });
    return response.data;
  } catch (error) {
    parseError(error);
  }
}

export async function iptalTopluAktivasyon(jobId) {
  try {
    const response = await axios.delete(`${BASE}/aktivasyon-maili/toplu/${encodeURIComponent(jobId)}`, { headers: getAuthHeader() });
    return response.data;
  } catch (error) {
    parseError(error);
  }
}

export async function updateUserGroup(id, userGroupId) {
  try {
    const response = await axios.put(
      `${BASE}/${id}/group`,
      { userGroupId },
      { headers: getAuthHeader() }
    );
    return response.data;
  } catch (error) {
    parseError(error);
  }
}

// ⚠️ SAYFA ÇAĞIRMAZ (#545): backend userActive'i 0 yapar AMA kullanıcının user_to_user_group / user_to_fuar / user_to_firma
// satırlarını da kalıcı siler (UserService.deleteUser). Pasifleştirme için `updateUser(id, { userActive: 0 })`.
export async function deleteUser(id) {
  try {
    const response = await axios.delete(`${BASE}/delete/${id}`, { headers: getAuthHeader() });
    return response.data;
  } catch (error) {
    parseError(error);
  }
}
